// assets/section-filters.js
if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.onSubmit = this.onSubmit.bind(this);
      this.onCollectionChange = this.onCollectionChange.bind(this);
      this.onFilterChange = this.onFilterChange.bind(this);
      this.onResultsClick = this.onResultsClick.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
      this.onDisclaimerAgree = this.onDisclaimerAgree.bind(this);
      this.onDisclaimerClose = this.onDisclaimerClose.bind(this);
      this.onDisclaimerKeydown = this.onDisclaimerKeydown.bind(this);

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.selects = this.querySelectorAll("select.dropdown-filter");

      this.resultsTarget = document.getElementById(
        this.dataset.inlineResultsTarget || "",
      );
      this.resultsStatus = this.resultsTarget?.querySelector(
        "[data-results-status]",
      );
      this.resultsBody = this.resultsTarget?.querySelector(
        "[data-results-body]",
      );

      this.disclaimerModal = document.getElementById(
        this.dataset.disclaimerModalTarget || "",
      );
      this.disclaimerAgreeButton = this.disclaimerModal?.querySelector(
        "[data-disclaimer-agree]",
      );
      this.disclaimerCloseButtons = this.disclaimerModal?.querySelectorAll(
        "[data-disclaimer-close]",
      );
      this.pendingDiagnosisSubmit = false;
      this.pendingDiagnosisSubmit = false;
      this.disclaimerSessionKey = `oilDiagnosisDisclaimerAccepted:${this.dataset.inlineResultsTarget || "default"}`;
      this.diagnosisStateSessionKey = "oilDiagnosisLatestState";
      this.isRestoringDiagnosisState = false;

      this.setupEventListeners();

      if (this.resultsTarget) {
        this.resultsTarget.addEventListener("click", this.onResultsClick);
      }

      if (this.disclaimerAgreeButton) {
        this.disclaimerAgreeButton.addEventListener(
          "click",
          this.onDisclaimerAgree,
        );
      }

      if (this.disclaimerCloseButtons?.length) {
        this.disclaimerCloseButtons.forEach((button) => {
          button.addEventListener("click", this.onDisclaimerClose);
        });
      }

      if (this.disclaimerModal) {
        this.disclaimerModal.addEventListener(
          "keydown",
          this.onDisclaimerKeydown,
        );
      }

      this.tabNavigation = this.closest(
        ".section-vertical-filters-form-wrap-inner",
      )?.querySelector(".tab-nav");

      if (this.tabNavigation && !this.select) {
        this.tabNavigation.addEventListener("click", this.onTabClick);
      }

      if (!this.select) {
        if (this.isCurrentTabVisible()) {
          this.restoreDiagnosisStateOrPopulate(true);
        } else {
          this.resetDiagnosisForm(true);
        }
      } else {
        this.populateCollectionFilters(true);
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.onSubmit);
      this.select?.removeEventListener("change", this.onCollectionChange);
      this.selects.forEach((select) => {
        select.removeEventListener("change", this.onFilterChange);
      });
      this.resultsTarget?.removeEventListener("click", this.onResultsClick);
      this.tabNavigation?.removeEventListener("click", this.onTabClick);
      this.disclaimerAgreeButton?.removeEventListener(
        "click",
        this.onDisclaimerAgree,
      );
      this.disclaimerCloseButtons?.forEach((button) => {
        button.removeEventListener("click", this.onDisclaimerClose);
      });
      this.disclaimerModal?.removeEventListener(
        "keydown",
        this.onDisclaimerKeydown,
      );
    }

    setupEventListeners() {
      this.form?.addEventListener("submit", this.onSubmit);
      this.select?.addEventListener("change", this.onCollectionChange);
      this.selects.forEach((select) => {
        select.addEventListener("change", this.onFilterChange);
      });
    }

    isDiagnosisMode() {
      return !this.select;
    }

    isCurrentTabVisible() {
      const tabContent = this.closest(".tabs-component-content");
      return tabContent?.getAttribute("aria-hidden") !== "true";
    }

    getDiagnosisApiBase() {
      return (
        window.OIL_DIAGNOSIS_API_BASE ||
        this.dataset.apiBase ||
        "http://localhost:3001"
      ).replace(/\/$/, "");
    }

    getDiagnosisVehicleType() {
      const tabContent = this.closest(".tabs-component-content");
      const handle = tabContent?.dataset.handle || "";

      if (handle.includes("tab-filters-nav-1")) {
        return "2wheeler";
      }

      return "4wheeler";
    }

    getDiagnosisSelections() {
      const getValue = (label) =>
        this.querySelector(`select[data-label="${label}"]`)?.value?.trim() ||
        "";

      return {
        vehicleType: this.getDiagnosisVehicleType(),
        manufacturer: getValue("car_manufacturer"),
        carModel: getValue("car_model"),
        engineModel: getValue("car_engine_model"),
        modelYear: getValue("car_model_year"),
      };
    }

    buildDiagnosisNavigationUrl() {
      const selections = this.getDiagnosisSelections();
      const url = new URL("/collections/all", window.location.origin);

      url.searchParams.set("diagnosis", "1");
      url.searchParams.set("vehicleType", selections.vehicleType);
      url.searchParams.set("manufacturer", selections.manufacturer);
      url.searchParams.set("carModel", selections.carModel);
      url.searchParams.set("engineModel", selections.engineModel);
      url.searchParams.set("modelYear", selections.modelYear);

      return url.toString();
    }

    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    getProductUrl(product) {
      if (!product?.handle) {
        return "#";
      }

      return `/products/${product.handle}`;
    }

    getProductImageUrl(product) {
      return product?.imageUrl || product?.featured_image || "";
    }

    formatMoney(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents)) {
        return "";
      }

      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
      }).format(cents / 100);
    }

    formatPriceText(productJson) {
      if (!productJson || typeof productJson.price_min !== "number") {
        return "";
      }

      const basePrice = this.formatMoney(productJson.price_min);

      if (productJson.price_varies) {
        return `${basePrice}〜`;
      }

      return basePrice;
    }

    buildSpecsText(product) {
      const specs = [];

      const candidates = [
        product?.spec,
        product?.standard,
        product?.api,
        product?.ilsac,
        product?.jaso,
        product?.acea,
      ];

      candidates.forEach((value) => {
        if (!value) {
          return;
        }

        String(value)
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .forEach((item) => specs.push(item));
      });

      return [...new Set(specs)].join(" - ");
    }

    getDiagnosisStockState(product) {
      if (!product) {
        return "";
      }

      if (product.available) {
        return "in_stock";
      }

      return "sold_out";
    }

    buildDiagnosisStockMarkup(product) {
      const stockState = this.getDiagnosisStockState(product);

      if (stockState === "in_stock") {
        return `
          <div class="badge-stock badge-stock-in">
            <span class="badge-stock-icon"></span> 在庫あり
          </div>
        `;
      }

      if (stockState === "sold_out") {
        return `
          <div class="badge-stock badge-stock-out">
            <span class="badge-stock-icon"></span> SOLD OUT
          </div>
        `;
      }

      return "";
    }

    getNoResultsNavigationConfig() {
      const vehicleType = this.getDiagnosisVehicleType();

      if (vehicleType === "2wheeler") {
        return {
          message: "ご入力条件に一致する推奨商品は見つかりませんでした。",
          description:
            "用途に合わせたオイルを、以下のカテゴリからご覧いただけます。",
          categories: [
            { label: "4ストローク向け", url: "/c/2w-engine-4stroke" },
            { label: "2ストローク向け", url: "/c/2w-engine-2stroke" },
            { label: "スクーター向け", url: "/c/2w-engine-scooter" },
            { label: "高性能・レース向け", url: "/c/2w-engine-race" },
          ],
          resetLabel: "エンジンオイル適格診断をやり直す",
          resetUrl: "/#vertical-filter",
        };
      }

      return {
        message: "ご入力条件に一致する推奨商品は見つかりませんでした。",
        description:
          "用途に合わせたオイルを、以下のカテゴリからご覧いただけます。",
        categories: [
          { label: "ガソリン車用", url: "/c/4w-engine-gasoline" },
          { label: "ディーゼル車用", url: "/c/4w-engine-diesel" },
          { label: "ハイブリッド／省燃費対応", url: "/c/4w-engine-hybrid" },
          { label: "高性能・スポーツ向け", url: "/c/4w-engine-sports" },
          { label: "クラッシックカー向け", url: "/c/4w-engine-classic" },
        ],
        resetLabel: "エンジンオイル適格診断をやり直す",
        resetUrl: "/#vertical-filter",
      };
    }

    buildNoResultsNavigationMarkup() {
      const config = this.getNoResultsNavigationConfig();
      const categoryLinks = config.categories
        .map((category) => {
          const label = this.escapeHtml(category.label);
          const url = this.escapeHtml(category.url);

          return `
            <a
              class="oil-diagnosis-section-chip"
              href="${url}"
              style="
                justify-content: center;
                padding: 0.9rem 1.8rem;
                font-size: 1.35rem;
                font-weight: 700;
                min-height: 4.2rem;
              "
            >
              ${label}
            </a>
          `;
        })
        .join("");

      return `
        <div
          class="oil-diagnosis-results-empty"
          style="
            text-align: center;
            padding: 3.6rem 0 4.2rem;
          "
        >
          <div
            class="oil-diagnosis-results-placeholder"
            style="
              padding: 0;
              margin: 0 0 2rem;
              font-size: 2.1rem;
              line-height: 1.6;
              font-weight: 700;
            "
          >
            ${this.escapeHtml(config.message)}
          </div>
          <div
            class="oil-diagnosis-results-note"
            style="
              margin: 0 0 2.4rem;
              font-size: 1.35rem;
              line-height: 1.9;
            "
          >
            ${this.escapeHtml(config.description)}
          </div>
          <div
            class="oil-diagnosis-section-labels"
            style="
              justify-content: center;
              align-items: center;
              gap: 1.2rem;
              margin-bottom: 2.8rem;
            "
          >
            ${categoryLinks}
          </div>
          <div
            class="table-line"
            style="
              display: flex;
              justify-content: center;
              margin: 0;
            "
          >
            <a
              href="${this.escapeHtml(config.resetUrl)}"
              class="button button-secondary button-solid"
              style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: auto;
                min-width: 0;
                margin: 0 auto;
                padding: 1.2rem 2.4rem;
                font-size: 1.4rem;
                font-weight: 700;
                line-height: 1.2;
              "
            >
              ${this.escapeHtml(config.resetLabel)}
            </a>
          </div>
        </div>
      `;
    }

    async fetchDiagnosisProductJson(handle) {
      const response = await fetch(`/products/${handle}.js`, {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch diagnosis product: ${handle}`);
      }

      return response.json();
    }

    async enrichDiagnosisProducts(products) {
      const items = Array.isArray(products) ? products : [];

      const entries = await Promise.all(
        items.map(async (item) => {
          if (!item?.handle) {
            return item;
          }

          try {
            const productJson = await this.fetchDiagnosisProductJson(
              item.handle,
            );

            return {
              ...item,
              title: item.title || productJson.title || "",
              brand: item.brand || productJson.vendor || "",
              imageUrl: item.imageUrl || productJson.featured_image || "",
              priceText: this.formatPriceText(productJson),
              available:
                typeof productJson.available === "boolean"
                  ? productJson.available
                  : item.available,
              variants: Array.isArray(productJson.variants)
                ? productJson.variants
                : item.variants,
            };
          } catch (error) {
            return {
              ...item,
              priceText: item.priceText || "",
            };
          }
        }),
      );

      return entries;
    }

    setFormUrl(url, background) {
      this.form?.setAttribute("action", url);

      if (!background && this.button) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        select.removeChild(option);
      });

      const defaultOption = select.querySelector("option");
      if (defaultOption) {
        defaultOption.selected = true;
      }
    }

    fillSelectOptions(select, values) {
      this.selectRemoveOptions(select);

      values.forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });
    }

    resetResults() {
      if (!this.resultsTarget || !this.resultsBody) {
        return;
      }

      this.resultsTarget.hidden = true;
      this.resultsBody.innerHTML = "";

      if (this.resultsStatus) {
        this.resultsStatus.textContent = "";
      }
    }

    showLoadingResults() {
      if (!this.resultsTarget || !this.resultsBody) {
        return;
      }

      this.resultsTarget.hidden = false;
      this.resultsBody.innerHTML =
        '<div class="oil-diagnosis-results-placeholder">検索結果を読み込んでいます…</div>';

      if (this.resultsStatus) {
        this.resultsStatus.textContent = "";
      }
    }

    showDiagnosisValidationMessage() {
      if (!this.resultsTarget || !this.resultsBody) {
        return;
      }

      this.resultsTarget.hidden = false;
      this.resultsBody.innerHTML =
        '<div class="oil-diagnosis-results-placeholder">メーカー・車種・エンジン型式・年式を選択してください。</div>';

      if (this.resultsStatus) {
        this.resultsStatus.textContent = "";
      }
    }

    isDisclaimerAcceptedForSession() {
      try {
        return sessionStorage.getItem(this.disclaimerSessionKey) === "1";
      } catch (error) {
        return false;
      }
    }

    setDisclaimerAcceptedForSession() {
      try {
        sessionStorage.setItem(this.disclaimerSessionKey, "1");
      } catch (error) {
        return;
      }
    }

    clearDisclaimerAcceptedForSession() {
      try {
        sessionStorage.removeItem(this.disclaimerSessionKey);
      } catch (error) {
        return;
      }
    }

    readSavedDiagnosisState() {
      try {
        const rawState = sessionStorage.getItem(this.diagnosisStateSessionKey);

        if (!rawState) {
          return null;
        }

        const state = JSON.parse(rawState);

        if (!state?.selections || !state?.result) {
          return null;
        }

        return state;
      } catch (error) {
        return null;
      }
    }

    saveDiagnosisState(selections, result) {
      try {
        sessionStorage.setItem(
          this.diagnosisStateSessionKey,
          JSON.stringify({
            selections,
            result,
            savedAt: Date.now(),
          }),
        );
      } catch (error) {
        return;
      }
    }

    clearDiagnosisState() {
      try {
        sessionStorage.removeItem(this.diagnosisStateSessionKey);
      } catch (error) {
        return;
      }
    }

    setDiagnosisSelectValue(label, value) {
      const select = this.querySelector(`select[data-label="${label}"]`);

      if (!select || !value) {
        return false;
      }

      const hasOption = Array.from(select.options).some(
        (option) => option.value === value,
      );

      if (!hasOption) {
        return false;
      }

      select.value = value;
      select.disabled = false;
      select.closest(".form-group")?.classList.remove("disabled");

      return true;
    }

    async restoreDiagnosisState() {
      if (!this.isDiagnosisMode() || !this.isCurrentTabVisible()) {
        return false;
      }

      const state = this.readSavedDiagnosisState();

      if (!state) {
        return false;
      }

      const selections = state.selections;

      if (selections.vehicleType !== this.getDiagnosisVehicleType()) {
        return false;
      }

      if (
        !selections.manufacturer ||
        !selections.carModel ||
        !selections.engineModel ||
        !selections.modelYear
      ) {
        return false;
      }

      this.isRestoringDiagnosisState = true;

      try {
        this.resetDiagnosisForm(true);

        const restoreSteps = [
          {
            label: "car_manufacturer",
            value: selections.manufacturer,
          },
          {
            label: "car_model",
            value: selections.carModel,
          },
          {
            label: "car_engine_model",
            value: selections.engineModel,
          },
          {
            label: "car_model_year",
            value: selections.modelYear,
          },
        ];

        for (const step of restoreSteps) {
          const options = await this.fetchDiagnosisOptions();
          this.applyDiagnosisOptions(options);

          if (!this.setDiagnosisSelectValue(step.label, step.value)) {
            this.clearDiagnosisState();
            this.resetDiagnosisForm(true);
            return false;
          }
        }

        const finalOptions = await this.fetchDiagnosisOptions();
        this.applyDiagnosisOptions(finalOptions);

        restoreSteps.forEach((step) => {
          this.setDiagnosisSelectValue(step.label, step.value);
        });

        this.renderDiagnosisResults(state.result, false);

        return true;
      } catch (error) {
        this.clearDiagnosisState();
        this.resetDiagnosisForm(true);
        return false;
      } finally {
        this.isRestoringDiagnosisState = false;
      }
    }

    async restoreDiagnosisStateOrPopulate(background = false) {
      const restored = await this.restoreDiagnosisState();

      if (restored) {
        return;
      }

      await this.populateDiagnosisFilters(background);
    }

    getFocusableDisclaimerElements() {
      if (!this.disclaimerModal) {
        return [];
      }

      return Array.from(
        this.disclaimerModal.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    openDisclaimerModal() {
      if (!this.disclaimerModal) {
        this.fetchDiagnosisResult(true);
        return;
      }

      this.pendingDiagnosisSubmit = true;
      this.disclaimerModal.hidden = false;
      this.disclaimerModal.classList.add("is-open");
      document.body.classList.add("oil-diagnosis-modal-open");

      const focusableElements = this.getFocusableDisclaimerElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    closeDisclaimerModal() {
      if (!this.disclaimerModal) {
        return;
      }

      this.disclaimerModal.classList.remove("is-open");
      this.disclaimerModal.hidden = true;
      document.body.classList.remove("oil-diagnosis-modal-open");
      this.pendingDiagnosisSubmit = false;
      this.button?.focus();
    }

    onDisclaimerAgree() {
      if (!this.pendingDiagnosisSubmit) {
        this.closeDisclaimerModal();
        return;
      }

      this.setDisclaimerAcceptedForSession();

      if (this.disclaimerModal) {
        this.disclaimerModal.classList.remove("is-open");
        this.disclaimerModal.hidden = true;
      }

      document.body.classList.remove("oil-diagnosis-modal-open");
      this.pendingDiagnosisSubmit = false;
      this.fetchDiagnosisResult(true);
    }

    onDisclaimerClose(event) {
      const closeTrigger = event.target.closest("[data-disclaimer-close]");

      if (!closeTrigger) {
        return;
      }

      this.closeDisclaimerModal();
    }

    onDisclaimerKeydown(event) {
      if (!this.disclaimerModal || this.disclaimerModal.hidden) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.closeDisclaimerModal();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = this.getFocusableDisclaimerElements();
      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    scrollToResultsTarget() {
      if (!this.resultsTarget) {
        return;
      }

      const headerOffset = 120;
      const targetTop =
        this.resultsTarget.getBoundingClientRect().top +
        window.scrollY -
        headerOffset;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: "smooth",
      });
    }

    toSameOriginUrl(url, extraParams = {}) {
      const parsedUrl = new URL(url, window.location.origin);
      const sameOriginUrl = new URL(
        `${parsedUrl.pathname}${parsedUrl.search}`,
        window.location.origin,
      );

      Object.entries(extraParams).forEach(([key, value]) => {
        sameOriginUrl.searchParams.set(key, value);
      });

      return sameOriginUrl.toString();
    }

    toNavigationUrl(url) {
      const parsedUrl = new URL(url, window.location.origin);

      if (parsedUrl.origin === window.location.origin) {
        return parsedUrl.toString();
      }

      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    buildDiagnosisOptionsUrl() {
      const baseUrl = `${this.getDiagnosisApiBase()}/api/diagnosis/options`;
      const url = new URL(baseUrl);
      const selections = this.getDiagnosisSelections();

      url.searchParams.set("vehicleType", selections.vehicleType);

      if (selections.manufacturer) {
        url.searchParams.set("manufacturer", selections.manufacturer);
      }

      if (selections.carModel) {
        url.searchParams.set("carModel", selections.carModel);
      }

      if (selections.engineModel) {
        url.searchParams.set("engineModel", selections.engineModel);
      }

      if (selections.modelYear) {
        url.searchParams.set("modelYear", selections.modelYear);
      }

      return url.toString();
    }

    async fetchDiagnosisOptions() {
      const response = await fetch(this.buildDiagnosisOptionsUrl(), {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch diagnosis options: ${response.status}`,
        );
      }

      const json = await response.json();

      if (!json?.ok) {
        throw new Error(json?.error || "診断候補の取得に失敗しました。");
      }

      return json;
    }

    applyDiagnosisOptions(options) {
      const mappings = [
        { index: 1, key: "manufacturers" },
        { index: 2, key: "carModels" },
        { index: 3, key: "engineModels" },
        { index: 4, key: "modelYears" },
      ];

      mappings.forEach(({ index, key }) => {
        const select = Array.from(this.selects).find(
          (item) => Number(item.dataset.index) === index,
        );

        if (!select) {
          return;
        }

        const values = Array.isArray(options?.[key]) ? options[key] : [];
        const currentValue = select.value;

        this.fillSelectOptions(select, values);

        if (values.includes(currentValue)) {
          select.value = currentValue;
        }

        if (index === 1) {
          select.disabled = values.length === 0;
          select
            .closest(".form-group")
            ?.classList.toggle("disabled", values.length === 0);
          return;
        }

        const previousSelect = Array.from(this.selects).find(
          (item) => Number(item.dataset.index) === index - 1,
        );
        const shouldEnable =
          Boolean(previousSelect?.value) &&
          Array.isArray(values) &&
          values.length > 0;

        select.disabled = !shouldEnable;
        select
          .closest(".form-group")
          ?.classList.toggle("disabled", !shouldEnable);
      });
    }

    resetDiagnosisForm(background = false) {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);

        if (Number(select.dataset.index) === 1) {
          select.disabled = background ? true : false;
          select
            .closest(".form-group")
            ?.classList.toggle("disabled", background);
          return;
        }

        select.disabled = true;
        select.closest(".form-group")?.classList.add("disabled");
      });

      if (!background) {
        this.resetResults();
      }
    }

    async populateDiagnosisFilters(background = false) {
      if (!this.isCurrentTabVisible()) {
        this.resetDiagnosisForm(true);
        return;
      }

      if (!background) {
        this.toggleLoading(true);
      }

      try {
        const options = await this.fetchDiagnosisOptions();
        this.applyDiagnosisOptions(options);
      } finally {
        if (!background) {
          this.toggleLoading(false);
        }
      }
    }

    async onDiagnosisFilterChange(event) {
      const selectedIndex = Number(event.target.dataset.index || 0);

      if (!this.isRestoringDiagnosisState) {
        this.clearDiagnosisState();
      }

      this.resetResults();

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= selectedIndex) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group")?.classList.add("disabled");
      });

      this.toggleLoading(true);

      try {
        const options = await this.fetchDiagnosisOptions();
        this.applyDiagnosisOptions(options);
      } finally {
        this.toggleLoading(false);
      }
    }

    onTabClick(event) {
      const tabItem = event.target.closest(".tab-nav-item");

      if (!tabItem) {
        return;
      }

      requestAnimationFrame(() => {
        const parentWrap = this.closest(
          ".section-vertical-filters-form-wrap-inner",
        );

        if (!parentWrap) {
          return;
        }

        const components = parentWrap.querySelectorAll("collections-filtering");

        components.forEach((component) => {
          if (!(component instanceof CollectionsFiltering)) {
            return;
          }

          component.resetResults();
          component.closeDisclaimerModal();

          if (component.isCurrentTabVisible()) {
            component.resetDiagnosisForm(true);
            component.restoreDiagnosisStateOrPopulate(true);
          } else {
            component.resetDiagnosisForm(true);
          }
        });
      });
    }

    buildDiagnosisNotice(type) {
      if (type !== "usable") {
        return "";
      }

      return `
        <div class="oil-diagnosis-results-note">
          <div>・メーカー推奨粘度とは異なります</div>
          <div>・ご使用前に車両取扱説明書をご確認ください</div>
        </div>
      `;
    }

    buildDiagnosisSectionLabel(title) {
      return `
        <div class="oil-diagnosis-section-labels">
          <span class="oil-diagnosis-section-chip">${this.escapeHtml(title)}</span>
        </div>
      `;
    }

    buildDiagnosisSection(type, title, products) {
      const items = Array.isArray(products) ? products : [];

      if (items.length === 0) {
        return "";
      }

      const cards = items
        .map((product) => {
          const titleText = this.escapeHtml(product.title || "");
          const brandText = this.escapeHtml(product.brand || "");
          const specsText = this.escapeHtml(this.buildSpecsText(product));
          const viscosityText = this.escapeHtml(product.viscosity || "");
          const capacityText = this.escapeHtml(product.capacity || "");
          const priceText = this.escapeHtml(product.priceText || "");
          const imageUrl = this.escapeHtml(this.getProductImageUrl(product));
          const productUrl = this.escapeHtml(this.getProductUrl(product));
          const stockMarkup = this.buildDiagnosisStockMarkup(product);

          return `
            <li class="grid-item oil-diagnosis-grid-item">
              <product-card class="card card-product card-product-no-rating oil-diagnosis-result-card">
                <figure class="card-media">
                  <a href="${productUrl}" class="js-product-link" aria-label="${titleText}">
                    <div class="aspect-ratio" style="--ratio-percent: 100%;">
                      ${
                        imageUrl
                          ? `<img src="${imageUrl}" alt="${titleText}" loading="lazy" class="card-media-image">`
                          : `<div class="card-media-image"></div>`
                      }
                    </div>
                  </a>
                </figure>
                <div class="card-info-wrap">
                  <div class="card-info">
                    ${
                      brandText
                        ? `<div class="card-eyebrow oil-diagnosis-card-brand">${brandText}</div>`
                        : ""
                    }
                    <h3 class="card-heading oil-diagnosis-card-title">
                      <a class="js-product-link" href="${productUrl}">${titleText}</a>
                    </h3>
                    ${
                      specsText || viscosityText || capacityText
                        ? `
                          <p class="card-spec oil-diagnosis-card-spec">
                            ${
                              specsText
                                ? `規格：<span>${specsText}</span><br>`
                                : ""
                            }
                            ${
                              viscosityText
                                ? `粘度：<span>${viscosityText}</span><br>`
                                : ""
                            }
                            ${
                              capacityText
                                ? `容量：<span>${capacityText}</span>`
                                : ""
                            }
                          </p>
                        `
                        : ""
                    }
                    ${stockMarkup}
                  </div>
                  <div class="card-footer oil-diagnosis-card-footer">
                    ${
                      priceText
                        ? `
                          <div class="price oil-diagnosis-card-price">
                            <div class="price-regular">
                              <span class="price-item price-item-regular">${priceText}</span>
                            </div>
                          </div>
                        `
                        : `<div class="price oil-diagnosis-card-price"></div>`
                    }
                    <div class="card-floating-footer">
                      <div class="card-product-actions">
                        <div class="product-actions-add-to-cart">
                          <a href="${productUrl}" class="button button-secondary button-solid js-product-link">
                            詳細を見る
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </product-card>
            </li>
          `;
        })
        .join("");

      return `
        <section class="oil-diagnosis-product-section oil-diagnosis-product-section-${this.escapeHtml(type)}">
          ${this.buildDiagnosisSectionLabel(title)}
          ${this.buildDiagnosisNotice(type)}
          <div class="collection oil-diagnosis-collection">
            <ul class="grid product-grid grid-2-col grid-3-col-tablet grid-5-col-desktop oil-diagnosis-product-grid">
              ${cards}
            </ul>
          </div>
        </section>
      `;
    }

    renderDiagnosisResults(result, shouldScroll = false) {
      if (!this.resultsTarget || !this.resultsBody) {
        return;
      }

      const recommended = Array.isArray(result?.recommended)
        ? result.recommended
        : [];
      const usable = Array.isArray(result?.usable) ? result.usable : [];

      const recommendedMarkup = this.buildDiagnosisSection(
        "recommended",
        "推奨オイル",
        recommended,
      );
      const usableMarkup = this.buildDiagnosisSection(
        "usable",
        "使用可能オイル",
        usable,
      );

      this.resultsTarget.hidden = false;

      if (this.resultsStatus) {
        this.resultsStatus.textContent = "";
      }

      if (!recommendedMarkup && !usableMarkup) {
        this.resultsBody.innerHTML = this.buildNoResultsNavigationMarkup();
      } else {
        this.resultsBody.innerHTML = `
          ${recommendedMarkup}
          ${usableMarkup}
        `;
      }

      if (shouldScroll) {
        requestAnimationFrame(() => {
          this.scrollToResultsTarget();
        });
      }
    }

    async fetchDiagnosisResult(shouldScroll = false) {
      if (!this.resultsTarget || !this.resultsBody) {
        return;
      }

      const selections = this.getDiagnosisSelections();

      if (
        !selections.manufacturer ||
        !selections.carModel ||
        !selections.engineModel ||
        !selections.modelYear
      ) {
        this.showDiagnosisValidationMessage();
        return;
      }

      this.toggleLoading(true);
      this.showLoadingResults();

      try {
        const response = await fetch(
          `${this.getDiagnosisApiBase()}/api/diagnosis`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(selections),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch diagnosis result: ${response.status}`,
          );
        }

        const json = await response.json();

        if (!json?.ok) {
          throw new Error(json?.error || "診断に失敗しました。");
        }

        const enrichedRecommended = await this.enrichDiagnosisProducts(
          json.recommended || [],
        );
        const enrichedUsable = await this.enrichDiagnosisProducts(
          json.usable || [],
        );

        const enrichedResult = {
          ...json,
          recommended: enrichedRecommended,
          usable: enrichedUsable,
        };

        this.renderDiagnosisResults(enrichedResult, shouldScroll);
        this.saveDiagnosisState(selections, enrichedResult);
      } catch (error) {
        this.resultsTarget.hidden = false;

        if (this.resultsBody) {
          this.resultsBody.innerHTML =
            '<div class="oil-diagnosis-results-placeholder">診断結果の取得に失敗しました。</div>';
        }

        if (this.resultsStatus) {
          this.resultsStatus.textContent =
            error instanceof Error
              ? error.message
              : "診断結果の取得に失敗しました。";
        }

        throw error;
      } finally {
        this.toggleLoading(false);
      }
    }

    onSubmit(event) {
      event.preventDefault();

      if (this.isDiagnosisMode()) {
        const selections = this.getDiagnosisSelections();

        if (
          !selections.manufacturer ||
          !selections.carModel ||
          !selections.engineModel ||
          !selections.modelYear
        ) {
          this.showDiagnosisValidationMessage();
          return;
        }

        if (this.isDisclaimerAcceptedForSession()) {
          this.fetchDiagnosisResult(true);
          return;
        }

        this.openDisclaimerModal();
        return;
      }

      const targetUrl = this.form?.getAttribute("action");

      if (!targetUrl) {
        return;
      }

      if (!this.resultsTarget || !this.resultsBody) {
        window.location = this.toNavigationUrl(targetUrl);
        return;
      }

      this.fetchAndRenderResults(targetUrl, true);
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ??
        this.input?.value ??
        window.routes.all_products_url;

      const url = new URL(collectionUrl, window.requestShopUrl);

      this.querySelectorAll("select.dropdown-filter:enabled").forEach(
        (select) => {
          if (select.value === "") {
            return;
          }

          Array.from(select.selectedOptions).forEach((option) => {
            const paramName =
              option.dataset.paramName || select.dataset.paramName || "";

            if (!paramName) {
              return;
            }

            url.searchParams.set(paramName, option.value);
          });
        },
      );

      return url.toString();
    }

    async fetchAndRenderResults(url, shouldScroll = false) {
      if (!this.resultsTarget || !this.resultsBody) {
        window.location = this.toNavigationUrl(url);
        return;
      }

      this.toggleLoading(true);
      this.showLoadingResults();

      try {
        const requestUrl = this.toSameOriginUrl(url);

        const response = await fetch(requestUrl, {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch results: ${response.status}`);
        }

        const text = await response.text();
        const resultsMarkup = new DOMParser().parseFromString(
          text,
          "text/html",
        );
        const productGridContainer = resultsMarkup.querySelector(
          "#ProductGridContainer",
        );
        const productGrid = resultsMarkup.querySelector("#product-grid");

        if (!productGridContainer || !productGrid) {
          throw new Error("Product grid markup was not found.");
        }

        this.resultsBody.innerHTML = productGridContainer.innerHTML;
        this.resultsTarget.hidden = false;

        if (this.resultsStatus) {
          if (productGrid.classList.contains("collection-empty")) {
            this.resultsStatus.textContent =
              "条件に一致する商品が見つかりませんでした。";
          } else {
            this.resultsStatus.textContent =
              "条件に一致した商品を表示しています。";
          }
        }

        if (shouldScroll) {
          requestAnimationFrame(() => {
            this.scrollToResultsTarget();
          });
        }
      } catch (error) {
        window.location = this.toNavigationUrl(url);
        throw error;
      } finally {
        this.toggleLoading(false);
      }
    }

    async onFilterChange(event) {
      if (this.isDiagnosisMode()) {
        this.onDiagnosisFilterChange(event);
        return;
      }

      const selectedIndex = event.target.dataset.index;

      this.resetResults();

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group")?.classList.add("disabled");
      });

      this.setFormUrl(this.constructURL());

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      this.toggleLoading(true);

      try {
        const requestUrl = this.toSameOriginUrl(url.toString());

        const response = await fetch(requestUrl, {
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(response.status);
        }

        const text = await response.text();
        const resultsMarkup = new DOMParser().parseFromString(
          text,
          "text/html",
        );

        this.selects.forEach((select) => {
          if (Number(select.dataset.index) <= Number(selectedIndex)) {
            return;
          }

          const options = resultsMarkup.querySelectorAll(
            `select[data-label="${select.dataset.label}"] option`,
          );

          this.selectRemoveOptions(select);

          if (options.length > 0) {
            const availableOptions = Array.from(options).filter(
              (option) => Number(option.dataset.count) > 0,
            );
            select.append(...availableOptions);
            const defaultOption = select.querySelector("option.default");
            if (defaultOption) {
              defaultOption.selected = true;
            }
          }

          if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
            select.disabled = false;
            select.closest(".form-group")?.classList.remove("disabled");
          }
        });
      } finally {
        this.toggleLoading(false);
      }
    }

    async populateCollectionFilters(background = false) {
      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");

      if (!background) {
        this.toggleLoading(true);
      }

      try {
        const requestUrl = this.toSameOriginUrl(url.toString());

        const response = await fetch(requestUrl, {
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(response.status);
        }

        const text = await response.text();
        const resultsMarkup = new DOMParser().parseFromString(
          text,
          "text/html",
        );

        this.selects.forEach((select) => {
          const options = resultsMarkup.querySelectorAll(
            `select[data-label="${select.dataset.label}"] option`,
          );

          this.selectRemoveOptions(select);

          if (options.length > 0) {
            const availableOptions = Array.from(options).filter(
              (option) => Number(option.dataset.count) > 0,
            );
            select.append(...availableOptions);
            const defaultOption = select.querySelector("option.default");
            if (defaultOption) {
              defaultOption.selected = true;
            }
          }

          if (Number(select.dataset.index) === 1) {
            select.disabled = false;
            select.closest(".form-group")?.classList.remove("disabled");
          }
        });
      } finally {
        if (!background) {
          this.toggleLoading(false);
        }
      }
    }

    onCollectionChange() {
      this.resetResults();

      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group")?.classList.add("disabled");
      });

      this.populateCollectionFilters();
    }

    onResultsClick(event) {
      const paginationLink = event.target.closest(".pagination a");

      if (!paginationLink || !this.resultsTarget?.contains(paginationLink)) {
        return;
      }

      event.preventDefault();
      this.fetchAndRenderResults(paginationLink.href, false);
    }

    toggleLoading(force) {
      const isLoading =
        typeof force === "boolean"
          ? force
          : !this.classList.contains("is-loading");

      this.classList.toggle("is-loading", isLoading);
      this.button?.classList.toggle("loading", isLoading);

      if (this.button) {
        this.button.disabled = isLoading;
        this.button.setAttribute("aria-busy", String(isLoading));
      }
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
